import { CoBrandingLogo } from '../../assets/Logo/CoBranding/CoBrandingLogo';
import { Logo, LogoDestination, LogoSizes } from '../../assets/Logo/Logo';
import { SideContactUsButton } from '../../components/SubmitFeedback/SideContactUsButton';
import { AppsMenu } from '../../components/apps-menu/Menu';
import { getOrCreateFeatureFlag } from '../../config/FeatureFlag';
import { UserMenuPopup } from '../../pages/common/UserMenu/UserMenuPopup';
import { theme } from '../../theme/theme';
import { useNavinaData } from '../../utils/useNavinaData';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ConfigProvider, Layout, theme as antdTheme } from 'antd';
import { observer } from 'mobx-react-lite';
import { Suspense, type ComponentProps, type ComponentType, type ReactNode } from 'react';
import { Outlet } from 'react-router';

export const HEADER_BACKGROUND_COLOR = theme.colors.primary;

export const BaseHeader = css`
	background-color: ${HEADER_BACKGROUND_COLOR};
	color: ${theme.font.colors.primaryDarkBackgroundContrast};
`;

const StyledLayout = styled(Layout)`
	block-size: 100%;
`;

const HEADER_MINIMUM_HEIGHT = '80px';

export const StyledHeader = styled.div`
	flex-wrap: nowrap;
	flex-shrink: 0;
	position: relative;
	${BaseHeader};
	padding-block: ${theme.paddings.base};
	padding-inline: ${theme.paddings.lg};

	min-block-size: ${HEADER_MINIMUM_HEIGHT};

	column-gap: ${theme.paddings.xxs};

	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`;

const HeaderButtonsContainer = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	column-gap: ${theme.paddings.xs};
`;

const PageTitle = styled.span`
	font-weight: ${theme.font.weights.bold};
	font-size: ${theme.font.sizes.title4};
	color: ${theme.colors.white};
`;

const HeaderLogoAndTitleContainer = styled(HeaderButtonsContainer)`
	column-gap: ${theme.paddings.lg};
`;

const Divider = styled.div`
	inline-size: 1px;
	align-self: stretch;
	margin-block: -2px;
	background: currentColor;
	margin-inline: ${theme.paddings.xs};
	opacity: 0.5;
	border-radius: ${theme.border.radii.sm};
`;

const LogosContainer = styled.div`
	display: inline-flex;
	align-items: center;
`;

const MainHeaderContainer = styled.div`
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
`;

const LayoutContent = styled(Layout.Content)`
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
	overflow-y: auto;
`;

export interface LayoutHeaderProps {
	readonly headerLogoLink?: LogoDestination;
	readonly headerAppMenuSelectedPage?: ComponentProps<typeof AppsMenu>['selectedPage'];
	readonly sideHeaderComponent?: ComponentType;
	readonly mainHeaderComponent?: ComponentType;
	readonly applicationsMenuComponent?: ReactNode;
	readonly headerTitle?: ReactNode;
}

interface BaseLayoutProps extends LayoutHeaderProps {
	readonly headerAppMenuSelectedPage: ComponentProps<typeof AppsMenu>['selectedPage'];
	readonly layoutContentProps?: ComponentProps<typeof Layout.Content>;
}

export const HeaderConfigProvider = function (props: ComponentProps<typeof ConfigProvider>) {
	const { primaryDarkBackgroundContrast, secondaryDarkBackgroundContrast, tertiaryDarkBackgroundContrast } =
		theme.font.colors;

	return (
		<ConfigProvider
			{...{
				theme: {
					algorithm: antdTheme.darkAlgorithm,
					components: {
						Button: {
							primaryShadow: '0',
							colorText: primaryDarkBackgroundContrast,
							colorTextDisabled: tertiaryDarkBackgroundContrast,
							colorLink: primaryDarkBackgroundContrast,
							colorLinkHover: secondaryDarkBackgroundContrast,
						},
						Form: {
							colorTextHeading: theme.colors.white,
						},
						Typography: {
							titleMarginBottom: '0',
							titleMarginTop: '0',
							colorTextHeading: theme.colors.white,
						},
					},
				},
				...props,
			}}
		/>
	);
};

const EmptyDiv = styled.div``;

export const BaseLayout = observer(function BaseLayout({
	layoutContentProps,
	headerLogoLink = LogoDestination.Schedule,
	headerAppMenuSelectedPage,
	sideHeaderComponent: SideHeaderComponent,
	mainHeaderComponent: MainHeaderComponent,
	applicationsMenuComponent = (
		<AppsMenu
			{...{
				selectedPage: headerAppMenuSelectedPage,
			}}
		/>
	),
	headerTitle,
	...props
}: BaseLayoutProps) {
	const { showFeedbackButton, coBrandingLogoUrl } = getOrCreateFeatureFlag();
	const { isOverlayOrEmbedded } = useNavinaData();

	const logoProps = {
		size: LogoSizes.Base,
		destination: headerLogoLink,
		disableForwardToPage: isOverlayOrEmbedded,
	} as const satisfies ComponentProps<typeof Logo>;

	return (
		<StyledLayout>
			<StyledHeader>
				<HeaderConfigProvider>
					<HeaderLogoAndTitleContainer>
						<LogosContainer>
							{Boolean(coBrandingLogoUrl) && (
								<>
									<CoBrandingLogo {...{ logoUrl: coBrandingLogoUrl, ...logoProps }} />
									<Divider />
								</>
							)}
							<Logo {...logoProps} />
						</LogosContainer>
						{Boolean(headerTitle) && <PageTitle>{headerTitle}</PageTitle>}
					</HeaderLogoAndTitleContainer>
					<MainHeaderContainer>
						{MainHeaderComponent && (
							<Suspense fallback={<EmptyDiv />}>
								<MainHeaderComponent {...props} />
							</Suspense>
						)}
					</MainHeaderContainer>
					<HeaderButtonsContainer>
						{SideHeaderComponent && (
							<Suspense fallback={<EmptyDiv />}>
								<SideHeaderComponent {...props} />
							</Suspense>
						)}
						<UserMenuPopup />
						{applicationsMenuComponent}
					</HeaderButtonsContainer>
				</HeaderConfigProvider>
			</StyledHeader>
			<LayoutContent {...layoutContentProps}>
				<Suspense fallback={<EmptyDiv />}>
					<Outlet />
				</Suspense>
			</LayoutContent>
			{showFeedbackButton && <SideContactUsButton />}
		</StyledLayout>
	);
});
